import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ADMIN_PAGE_SIZE } from '../admin/admin-api.js';
import { BackToAdmin, firstForbidden, Notice } from '../admin/admin-ui.js';
import { EmployeeForm } from '../admin/employee-form.js';
import { useCreateEmployee, useEmployeesPage } from '../admin/people-api.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { PageHeader } from '../components/page-header.js';
import { pageFrom, Pagination, PastLastPage, TABLE, TD, TH, usePageHref } from '../requests/request-list.js';

/** Botones de esta pantalla, con el aspecto de las ya adaptadas (`admin-ui.tsx` conserva los suyos). */
const PRIMARY = buttonClasses();
const OUTLINE = buttonClasses('outline');

/**
 * Empleados (W8): una página de `GET /employees` (nombre, correo, cargo y estado; nunca el Object ID en la
 * lista) y el alta (`POST /employees`, sin `Idempotency-Key`). Las reglas las decide la API: el Director
 * General único, el responsable obligatorio o los ciclos se muestran como 409 o 422.
 *
 * Igual que áreas y tipos de permiso: la tabla en una tarjeta con «Crear» en su cabecera y el alta en una
 * ventana (`Modal`) con el formulario compartido, que conserva sus clases. La ficha sigue en su página.
 */
export function AdminEmployeesPage() {
  const [params] = useSearchParams();
  const page = pageFrom(params, 'pagina');
  const pageHref = usePageHref('pagina');
  const employees = useEmployeesPage(page);
  const create = useCreateEmployee();
  const [creating, setCreating] = useState(false);
  const [saved, setSaved] = useState<string | undefined>(undefined);

  const denied = firstForbidden(employees.error, create.error);
  if (denied) return <ApiErrorView error={denied} />;

  const openCreate = () => {
    // Un error de un alta anterior no se arrastra a la nueva; el formulario se monta vacío.
    create.reset();
    setSaved(undefined);
    setCreating(true);
  };

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <BackToAdmin />
      <PageHeader title="Empleados" description="Altas, datos, roles y revocación de sesiones." />
      <Notice text={saved} />

      <div>
        {employees.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
        {employees.isError ? <ApiErrorView error={employees.error} /> : null}
        {employees.isSuccess ? (
          <>
            <Card flush>
              <div className="flex justify-end border-b border-border p-4">
                <button type="button" className={PRIMARY} onClick={openCreate}>
                  <Plus aria-hidden className="size-4" />
                  Crear
                </button>
              </div>
              {employees.data.total === 0 ? (
                <p className="px-5 py-16 text-center text-sm text-muted-foreground">Todavía no hay empleados.</p>
              ) : employees.data.items.length === 0 ? (
                <div className="p-5">
                  <PastLastPage firstPage={pageHref(1)} what="empleados" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={TABLE} aria-label="Empleados">
                    <thead className="border-b border-border bg-muted text-muted-foreground">
                      <tr>
                        <th scope="col" className={TH}>Nombre</th>
                        <th scope="col" className={TH}>Correo</th>
                        <th scope="col" className={TH}>Cargo</th>
                        <th scope="col" className={TH}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.data.items.map((employee) => (
                        <tr key={employee.id} className="border-t border-border transition-colors first:border-t-0 hover:bg-muted">
                          <th scope="row" className={TH}>
                            <Link to={`/administracion/empleados/${employee.id}`} className="text-primary underline-offset-4 hover:underline">
                              {employee.fullName}
                            </Link>
                          </th>
                          <td className={TD}>{employee.email}</td>
                          <td className={TD}>{employee.jobTitle ?? '—'}</td>
                          <td className={TD}>{employee.active ? 'Activo' : 'Inactivo'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {employees.data.total > 0 ? (
              <Pagination page={page} total={employees.data.total} pageSize={ADMIN_PAGE_SIZE} hrefFor={pageHref} units={['empleado', 'empleados']} />
            ) : null}
          </>
        ) : null}
      </div>

      {creating ? (
        <Modal title="Nuevo empleado" onClose={() => setCreating(false)}>
          <div className="space-y-4">
            {/* El formulario compartido (también lo usa la ficha) lleva su propio «Crear empleado». */}
            <EmployeeForm
              pending={create.isPending}
              error={create.error}
              submitLabel="Crear empleado"
              onCreate={(body) => {
                create.mutate(body, {
                  onSuccess: (employee) => {
                    setCreating(false);
                    setSaved(`Empleado «${employee.fullName}» creado.`);
                  },
                });
              }}
            />
            <ModalFooter>
              <button type="button" className={OUTLINE} onClick={() => setCreating(false)}>
                Cancelar
              </button>
            </ModalFooter>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
